import React from "react";
import { BookingAttachment } from "../types/ghlBookingChat";

interface AppointmentCardProps {
  data: BookingAttachment;
}

const confirmedIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
};

const notBookedIconStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-danger) 10%, transparent)",
};

const confirmedBadgeStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-primary) 12%, var(--color-surface))",
  color: "var(--color-primary)",
};

const notBookedBadgeStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-danger) 12%, var(--color-surface))",
  color: "var(--color-danger)",
};

const notBookedPanelStyle: React.CSSProperties = {
  background: "color-mix(in srgb, var(--color-danger) 6%, transparent)",
};

const BADGE_LABEL: Record<string, string> = {
  calendar_not_found: "Calendar Not Found",
  calendar_ambiguous: "Which Calendar?",
  slot_taken: "Slot Taken",
  not_connected: "Not Connected",
  needs_reauth: "Needs Reconnect",
  no_calendar: "No Calendar",
  missing_contact_info: "Missing Info",
  unavailable: "Unavailable",
};

const DEFAULT_MESSAGE: Record<string, string> = {
  calendar_not_found:
    "That calendar wasn't found on this account. Check the name and try again.",
  calendar_ambiguous:
    "That name matches more than one calendar — say which one you mean.",
  slot_taken: "That time was just taken. Ask for another time and try again.",
  not_connected: "This business hasn't connected a calendar yet — nothing was booked.",
  needs_reauth: "The calendar connection needs to be reconnected — nothing was booked.",
  no_calendar: "No active calendar is set up for this business — nothing was booked.",
  missing_contact_info: "An email or phone number is needed to book — nothing was scheduled yet.",
  unavailable: "Booking failed. Nothing was scheduled — try again shortly.",
};

/** GHL returns offset-bearing ISO times. Without `timeZone` the browser
 *  renders them in the VIEWER's zone: an appointment booked for 10:00 AM
 *  Central displayed as "11:00 PM" to a UTC+8 viewer, directly beneath a
 *  CONFIRMED badge. Same bug already fixed in AvailabilityCard and missed
 *  here, because only the availability symptom had been reported.
 *  Falls back to viewer-local when the backend sent no timezone. */
const formatWhen = (iso: string, timeZone?: string) => {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "the requested time";
  const opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  };
  if (timeZone) {
    try {
      return dt.toLocaleString(undefined, { ...opts, timeZone });
    } catch {
      // An unrecognised IANA name would throw RangeError and blank the card.
      return dt.toLocaleString(undefined, opts);
    }
  }
  return dt.toLocaleString(undefined, opts);
};

/** "America/Chicago" -> "Chicago". Keeps non Region/City ids (e.g. "UTC"). */
const shortZone = (timeZone?: string) =>
  timeZone ? timeZone.split("/").pop()!.replace(/_/g, " ") : "";

/** Renders ghl_book_appointment's result. Only status "confirmed" ever
 *  reads as booked — every other status renders as an unmistakable
 *  non-booking (struck-through time + a danger-tinted panel), never a
 *  neutral or ambiguous state. This is the fix for SGL-123: the backend
 *  used to say "treat the booking as confirmed" unconditionally. */
export const AppointmentCard: React.FC<AppointmentCardProps> = ({ data }) => {
  const { result } = data;
  const confirmed = result.status === "confirmed";

  return (
    <div
      className="my-1 w-full max-w-[420px] overflow-hidden rounded-2xl border bg-theme-surface"
      style={{ borderColor: confirmed ? "var(--color-border)" : "color-mix(in srgb, var(--color-danger) 25%, var(--color-border))" }}
    >
      <div className="flex items-center justify-between gap-2 border-b border-theme-border px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl"
            style={confirmed ? confirmedIconStyle : notBookedIconStyle}
          >
            <span
              className="material-icons-outlined text-[18px]"
              style={{ color: confirmed ? "var(--color-primary)" : "var(--color-danger)" }}
            >
              {confirmed ? "check_circle" : "event_busy"}
            </span>
          </span>
          <p className="truncate text-[13px] font-bold text-theme-fg">
            {confirmed ? "Appointment Confirmed" : "Not Booked"}
          </p>
        </div>
        <span
          className="flex-shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide"
          style={confirmed ? confirmedBadgeStyle : notBookedBadgeStyle}
        >
          {confirmed ? "Confirmed" : BADGE_LABEL[result.status] ?? "Unavailable"}
        </span>
      </div>

      {confirmed ? (
        <div className="px-4 pb-4 pt-3.5">
          <div className="flex items-center gap-2 text-[14px] font-bold text-theme-fg">
            <span className="material-icons-outlined text-[16px] text-theme-primary">schedule</span>
            {formatWhen(result.start_time, result.timezone)}
          </div>
          <p className="mt-1.5 text-[11.5px] text-theme-fg-muted">
            {result.duration_minutes} minutes
            {result.calendar_name ? ` · ${result.calendar_name}` : ""}
            {result.timezone ? ` · ${shortZone(result.timezone)} time` : ""}
          </p>
          <div className="mt-2.5 flex items-center justify-between gap-2 border-t border-dashed border-theme-border pt-2.5">
            <span className="text-[10.5px] uppercase tracking-wide text-theme-fg-muted">Confirmation</span>
            <span className="rounded-md border border-theme-border bg-theme-background px-2 py-0.5 font-mono text-[11.5px] text-theme-fg">
              {result.confirmation_id}
            </span>
          </div>
        </div>
      ) : (
        <div className="px-4 pb-4 pt-3.5">
          <div
            className="flex items-center gap-2 text-[13px] font-semibold text-theme-fg-muted"
            style={{ textDecoration: "line-through", textDecorationColor: "var(--color-danger)", textDecorationThickness: "1.5px" }}
          >
            <span className="material-icons-outlined text-[16px] text-theme-fg-muted">schedule</span>
            {formatWhen(result.start_time)}
          </div>
          <div className="mt-2.5 flex items-start gap-2 rounded-xl px-3 py-2.5" style={notBookedPanelStyle}>
            <span className="material-icons-outlined mt-0.5 flex-shrink-0 text-[16px]" style={{ color: "var(--color-danger)" }}>
              {result.status === "slot_taken" ? "event_busy" : result.status === "needs_reauth" ? "sync_problem" : "link_off"}
            </span>
            <p className="text-[12px] leading-relaxed" style={{ color: "var(--color-danger)" }}>
              {result.message || DEFAULT_MESSAGE[result.status] || DEFAULT_MESSAGE.unavailable}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
